import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ListChecks, RefreshCw, Search, Filter, X, History, Save, Check,
  ChevronRight, AlertCircle, CheckCircle2, RotateCcw, Undo2, PackageCheck,
  AlertTriangle, Package, MessageSquarePlus, ExternalLink,
} from "lucide-react";
import { useApp, can } from "@/context/AppContext";
import {
  pvWorkbench, pvInlineUpdate, pvHistory, pvRejects, pvStatuses,
  pvMarkDelivered, pvReturnToActive, pvReopenDelivered, pvUpdateParts, pvAddFollowup,
} from "@/lib/api";
import { LocTriggerModal } from "@/components/LocTriggerModal";
import { LocActiveBadge } from "@/components/LocActiveBadge";

const TABS = [
  { key: "active_pending",     label: "Active Pending" },
  { key: "ready_for_delivery", label: "Ready for Delivery" },
  { key: "delivered",          label: "Delivered" },
  { key: "rejected",           label: "Rejected Imports" },
];

const AGEING_BUCKETS = [
  { value: "",     label: "All Ageing" },
  { value: "0-3",  label: "0 - 3 days" },
  { value: "4-7",  label: "4 - 7 days" },
  { value: "8-15", label: "8 - 15 days" },
  { value: "15+",  label: "15+ days" },
];
const PENDING_DAYS_BUCKETS = [
  { value: "",     label: "All Pending Days" },
  { value: "0-3",  label: "0 - 3 days" },
  { value: "4-7",  label: "4 - 7 days" },
  { value: "8-15", label: "8 - 15 days" },
  { value: "15+",  label: "15+ days" },
];
const READY_SINCE_BUCKETS = [
  { value: "",       label: "All Ready Since" },
  { value: "today",  label: "Today" },
  { value: "1d",     label: "1 Day" },
  { value: "2d",     label: "2 Days" },
  { value: "gt2d",   label: "More than 2 Days" },
];

const AGE_TONE = (b) => ({
  "0-3":  "bg-emerald-50 text-emerald-700 border-emerald-200",
  "4-7":  "bg-amber-50 text-amber-700 border-amber-200",
  "8-15": "bg-orange-50 text-orange-700 border-orange-200",
  "15+":  "bg-rose-50 text-rose-700 border-rose-200",
}[b] || "bg-[#f4f3fc] text-[#444653] border-[#e2e1eb]");

const READY_TONE = (b) => ({
  "today": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "1d":    "bg-amber-50 text-amber-700 border-amber-200",
  "2d":    "bg-orange-50 text-orange-700 border-orange-200",
  "gt2d":  "bg-rose-50 text-rose-700 border-rose-200",
}[b] || "bg-[#f4f3fc] text-[#444653] border-[#e2e1eb]");

const READY_LABEL = (b, d) => {
  if (b === "today") return "Today";
  if (b === "1d")    return "1 Day";
  if (b === "2d")    return "2 Days";
  if (b === "gt2d")  return `${d} Days`;
  return "—";
};

const PvEmptyState = ({ msg }) => (
  <div className="bg-white border border-dashed border-[#d5d2e4] rounded-xl py-14 px-6 text-center" data-testid="pv-empty-state">
    <p className="text-[13px] text-[#747684] max-w-xl mx-auto">{msg}</p>
  </div>
);

const fmtDate = (iso) => {
  if (!iso) return "—";
  const s = String(iso).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const [y, m, d] = s.split("-");
  return `${d}/${m}/${y}`;
};
const fmtDT = (iso) => {
  if (!iso) return "—";
  try {
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) return iso;
    const dd = String(dt.getDate()).padStart(2, "0");
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const yy = dt.getFullYear();
    let h = dt.getHours();
    const mn = String(dt.getMinutes()).padStart(2, "0");
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    return `${dd}/${mm}/${yy} ${String(h).padStart(2, "0")}:${mn} ${ampm}`;
  } catch { return iso; }
};
const nowDate = () => new Date().toISOString().slice(0, 10);
const nowTime = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

const PARTS_STATUS_LABELS = { ordered: "Ordered", partially_received: "Partially Received", received: "Received" };

/** Safely renders a FastAPI/Pydantic error detail (string, object or array
 *  of validation errors) as a single readable line for toast messages. */
const formatErr = (e, fallback) => {
  const detail = e?.response?.data?.detail;
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => (d && typeof d.msg === "string" ? d.msg : JSON.stringify(d))).filter(Boolean).join(" ") || fallback;
  }
  if (detail.msg) return detail.msg;
  return fallback;
};

/** Compact parts indicator reused in the PV row's existing EDD area. */
const PartsIndicator = ({ row }) => {
  if (!row.parts_ordered) return null;
  const eddShort = row.parts_edd ? fmtDate(row.parts_edd).slice(0, 5) : null;
  const poLabel = row.parts_po_number ? `PO ${row.parts_po_number}` : "Parts Ordered";
  if (row.parts_status === "received") {
    return <div className="text-[10px] font-semibold text-emerald-700 mt-1" data-testid={`pv-row-parts-${row.id}`}>Parts Received</div>;
  }
  if (row.parts_status === "partially_received") {
    return (
      <div className="text-[10px] font-semibold text-amber-700 mt-1" data-testid={`pv-row-parts-${row.id}`}>
        Parts Partial{eddShort ? ` • EDD ${eddShort}` : ""}
      </div>
    );
  }
  return (
    <div className="text-[10px] font-semibold text-sky-700 mt-1" data-testid={`pv-row-parts-${row.id}`}>
      {poLabel}{eddShort ? ` • EDD ${eddShort}` : ""}
    </div>
  );
};

const PvWorkbench = () => {
  const { currentUser, locations } = useApp();
  const canWrite = can(currentUser, "pv_write");
  const canReopen = can(currentUser, "pv_reopen");
  const location = useLocation();
  const qp = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const allowedLocs = useMemo(
    () => locations.filter((l) => currentUser?.role === "admin" || (currentUser?.location_codes || []).includes(l.code)),
    [locations, currentUser],
  );

  const [tab, setTab] = useState("active_pending");
  const [locationCode, setLocationCode] = useState(qp.get("location_code") || "");
  const [q, setQ] = useState("");
  const [ageing, setAgeing] = useState(qp.get("ageing_bucket") || "");
  const [statusFilter, setStatusFilter] = useState("");
  // Ready-for-Delivery filter
  const [readySince, setReadySince] = useState("");
  // Delivered filters
  const [dJcdFrom, setDJcdFrom] = useState("");
  const [dJcdTo, setDJcdTo] = useState("");
  const [dDdFrom, setDDdFrom] = useState("");
  const [dDdTo, setDDdTo] = useState("");
  const [dDeliveredBy, setDDeliveredBy] = useState("");
  const [dVehicleModel, setDVehicleModel] = useState("");
  const [dPendingBucket, setDPendingBucket] = useState("");

  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);
  const [buffers, setBuffers] = useState({});
  const [saving, setSaving] = useState({});
  const [historyFor, setHistoryFor] = useState(null);
  const [rejectRows, setRejectRows] = useState([]);
  const [cnrFor, setCnrFor] = useState(null);

  // Dialog state
  const [deliverFor, setDeliverFor] = useState(null);
  const [returnFor, setReturnFor] = useState(null);
  const [reopenFor, setReopenFor] = useState(null);
  const [partsFor, setPartsFor] = useState(null);
  const [followupFor, setFollowupFor] = useState(null);
  const [historyRefreshTick, setHistoryRefreshTick] = useState(0);

  const loadStatuses = useCallback(async () => {
    try {
      const d = await pvStatuses(false);
      setStatusOptions(d.rows || []);
    } catch {/* silent */}
  }, []);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === "rejected") {
        const d = await pvRejects({ location_code: locationCode || undefined, q: q || undefined });
        setRejectRows(d.rows || []);
      } else {
        const params = {
          record_state: tab,
          location_code: locationCode || undefined,
          q: q || undefined,
          pv_status_id: statusFilter || undefined,
        };
        if (tab === "active_pending") params.ageing_bucket = ageing || undefined;
        if (tab === "ready_for_delivery") params.ready_since_bucket = readySince || undefined;
        if (tab === "delivered") {
          params.jcd_from = dJcdFrom || undefined;
          params.jcd_to = dJcdTo || undefined;
          params.dd_from = dDdFrom || undefined;
          params.dd_to = dDdTo || undefined;
          params.delivered_by = dDeliveredBy || undefined;
          params.vehicle_model = dVehicleModel || undefined;
          params.pending_days_bucket = dPendingBucket || undefined;
        }
        const d = await pvWorkbench(params);
        setRows(d.rows || []);
        setCounts(d.counts || {});
        setBuffers({});
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Pending Vehicles");
    } finally {
      setLoading(false);
    }
  }, [tab, locationCode, q, statusFilter, ageing, readySince,
      dJcdFrom, dJcdTo, dDdFrom, dDdTo, dDeliveredBy, dVehicleModel, dPendingBucket]);

  useEffect(() => { loadStatuses(); }, [loadStatuses]);
  useEffect(() => { loadRows(); }, [loadRows]);

  const loadCountsOnly = useCallback(async () => {
    try {
      const d = await pvWorkbench({ record_state: "active_pending" });
      setCounts(d.counts || {});
    } catch {/* silent */}
  }, []);
  useEffect(() => { if (tab === "rejected") loadCountsOnly(); }, [tab, loadCountsOnly]);

  const dirty = (row) => {
    const b = buffers[row.id];
    if (!b) return false;
    if (b.pv_status_id !== undefined && b.pv_status_id !== (row.pending_vehicle_status_id || "")) return true;
    if (b.expected_delivery_date !== undefined && (b.expected_delivery_date || "") !== (row.expected_delivery_date || "")) return true;
    if (b.latest_remark !== undefined && (b.latest_remark || "") !== (row.latest_remark || "")) return true;
    return false;
  };
  const setBuf = (id, patch) => setBuffers((b) => ({ ...b, [id]: { ...(b[id] || {}), ...patch } }));

  const saveRow = async (row) => {
    if (!canWrite) return;
    const b = buffers[row.id];
    if (!b) return;
    const selStatusId = b.pv_status_id !== undefined ? b.pv_status_id : (row.pending_vehicle_status_id || "");
    const sel = statusOptions.find((s) => s.id === selStatusId);
    const edd = b.expected_delivery_date !== undefined ? b.expected_delivery_date : (row.expected_delivery_date || "");
    const remark = b.latest_remark !== undefined ? b.latest_remark : (row.latest_remark || "");
    if (sel?.edd_mandatory && !(edd || "").trim()) return toast.error("EDD is required for the selected status.");
    if (sel?.remarks_mandatory && !(remark || "").trim()) return toast.error("Remark is required for the selected status.");
    setSaving((s) => ({ ...s, [row.id]: true }));
    try {
      const payload = {};
      if (b.pv_status_id !== undefined) payload.pv_status_id = b.pv_status_id || null;
      if (b.expected_delivery_date !== undefined) payload.expected_delivery_date = b.expected_delivery_date || null;
      if (b.latest_remark !== undefined) payload.latest_remark = b.latest_remark || null;
      const r = await pvInlineUpdate(row.id, payload);
      toast.success(r.record?.record_state !== row.record_state
        ? `Moved to ${TABS.find((t) => t.key === r.record.record_state)?.label || r.record.record_state}`
        : "Saved");
      loadRows();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving((s) => ({ ...s, [row.id]: false }));
    }
  };

  const clearAllFilters = () => {
    setQ(""); setLocationCode(""); setStatusFilter(""); setAgeing(""); setReadySince("");
    setDJcdFrom(""); setDJcdTo(""); setDDdFrom(""); setDDdTo("");
    setDDeliveredBy(""); setDVehicleModel(""); setDPendingBucket("");
  };
  const anyFilter = q || locationCode || statusFilter || ageing || readySince
    || dJcdFrom || dJcdTo || dDdFrom || dDdTo || dDeliveredBy || dVehicleModel || dPendingBucket;

  // ------- Header + Tabs -------
  const Header = (
    <div className="flex items-start gap-3">
      <div className="w-11 h-11 rounded-lg bg-yamaha-50 flex items-center justify-center">
        <ListChecks className="w-6 h-6 text-yamaha-700" />
      </div>
      <div className="flex-1">
        <h1 className="text-[22px] font-semibold text-yamaha-800 leading-none tracking-tight">
          PV Workbench
        </h1>
        <p className="text-[12.5px] text-[#747684] mt-1">
          {canWrite
            ? "Track pending job cards through Ready for Delivery to Delivered. Inline edits auto-advance the record state where the status allows it."
            : "View-only. Contact your Workshop Manager to update pending vehicles."}
        </p>
      </div>
      <button
        onClick={loadRows}
        data-testid="pv-wb-refresh"
        className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#e2e1eb] text-[12px] text-[#444653] hover:bg-white"
      >
        <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        Refresh
      </button>
    </div>
  );

  const Tabs = (
    <div className="border-b border-[#e2e1eb] flex items-center gap-1 overflow-x-auto">
      {TABS.map((t) => {
        const n = counts[t.key];
        return (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`pv-tab-${t.key}`}
            className={`px-3 py-2 text-[12.5px] font-semibold transition-colors border-b-2 -mb-px whitespace-nowrap flex items-center gap-1.5 ${
              tab === t.key
                ? "text-yamaha-700 border-yamaha-700"
                : "text-[#747684] border-transparent hover:text-[#1a1b22]"
            }`}
          >
            {t.label}
            {typeof n === "number" && (
              <span className={`text-[10.5px] font-mono px-1.5 py-0.5 rounded ${tab === t.key ? "bg-yamaha-50 text-yamaha-700" : "bg-[#f4f3fc] text-[#747684]"}`}>
                {n}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );

  const CommonFilters = (
    <>
      <div className="relative">
        <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#747684]" />
        <input
          data-testid="pv-wb-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search JC, Customer, VIN…"
          className="pl-8 pr-2 py-1.5 rounded-md border border-[#e2e1eb] text-[12px] w-[220px]"
        />
      </div>
      <select
        data-testid="pv-wb-location"
        value={locationCode}
        onChange={(e) => setLocationCode(e.target.value)}
        className="border border-[#e2e1eb] rounded-md px-2 py-1.5 text-[12px]"
      >
        <option value="">All Locations</option>
        {allowedLocs.map((l) => (
          <option key={l.code} value={l.code}>{l.name} ({l.code})</option>
        ))}
      </select>
    </>
  );

  const FiltersBar = (
    <div className="bg-white border border-[#e2e1eb] rounded-xl p-3 flex flex-wrap items-center gap-2">
      <Filter className="w-3.5 h-3.5 text-[#747684]" />
      {CommonFilters}
      {tab === "active_pending" && (
        <>
          <select data-testid="pv-wb-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-[#e2e1eb] rounded-md px-2 py-1.5 text-[12px]">
            <option value="">All Statuses</option>
            {statusOptions.map((s) => <option key={s.id} value={s.id}>{s.status_name}</option>)}
          </select>
          <select data-testid="pv-wb-ageing" value={ageing} onChange={(e) => setAgeing(e.target.value)}
            className="border border-[#e2e1eb] rounded-md px-2 py-1.5 text-[12px]">
            {AGEING_BUCKETS.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
          </select>
        </>
      )}
      {tab === "ready_for_delivery" && (
        <select data-testid="pv-wb-ready-since" value={readySince} onChange={(e) => setReadySince(e.target.value)}
          className="border border-[#e2e1eb] rounded-md px-2 py-1.5 text-[12px]">
          {READY_SINCE_BUCKETS.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
        </select>
      )}
      {tab === "delivered" && (
        <>
          <label className="text-[11px] text-[#747684]">JC Date</label>
          <input type="date" data-testid="pv-wb-jcd-from" value={dJcdFrom} onChange={(e) => setDJcdFrom(e.target.value)}
            className="border border-[#e2e1eb] rounded-md px-2 py-1.5 text-[12px]" />
          <span className="text-[11px] text-[#747684]">to</span>
          <input type="date" data-testid="pv-wb-jcd-to" value={dJcdTo} onChange={(e) => setDJcdTo(e.target.value)}
            className="border border-[#e2e1eb] rounded-md px-2 py-1.5 text-[12px]" />
          <label className="text-[11px] text-[#747684]">Delivered</label>
          <input type="date" data-testid="pv-wb-dd-from" value={dDdFrom} onChange={(e) => setDDdFrom(e.target.value)}
            className="border border-[#e2e1eb] rounded-md px-2 py-1.5 text-[12px]" />
          <span className="text-[11px] text-[#747684]">to</span>
          <input type="date" data-testid="pv-wb-dd-to" value={dDdTo} onChange={(e) => setDDdTo(e.target.value)}
            className="border border-[#e2e1eb] rounded-md px-2 py-1.5 text-[12px]" />
          <input data-testid="pv-wb-delivered-by" value={dDeliveredBy} onChange={(e) => setDDeliveredBy(e.target.value)}
            placeholder="Delivered By" className="border border-[#e2e1eb] rounded-md px-2 py-1.5 text-[12px] w-[140px]" />
          <input data-testid="pv-wb-vehicle-model" value={dVehicleModel} onChange={(e) => setDVehicleModel(e.target.value)}
            placeholder="Vehicle Model" className="border border-[#e2e1eb] rounded-md px-2 py-1.5 text-[12px] w-[140px]" />
          <select data-testid="pv-wb-pending-days" value={dPendingBucket} onChange={(e) => setDPendingBucket(e.target.value)}
            className="border border-[#e2e1eb] rounded-md px-2 py-1.5 text-[12px]">
            {PENDING_DAYS_BUCKETS.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
          </select>
        </>
      )}
      {anyFilter && (
        <button onClick={clearAllFilters} data-testid="pv-wb-clear"
          className="text-[11.5px] text-yamaha-700 hover:underline">Clear filters</button>
      )}
      <div className="flex-1"></div>
      <div className="text-[11.5px] text-[#747684]">
        {tab === "rejected" ? `${rejectRows.length} rejected` : `${rows.length} record${rows.length === 1 ? "" : "s"}`}
      </div>
    </div>
  );

  // ---- Rejected tab -------------------------------------------------
  if (tab === "rejected") {
    return (
      <div className="max-w-[1400px] mx-auto space-y-4" data-testid="pv-workbench-page">
        {Header} {Tabs} {FiltersBar}
        {loading ? <PvEmptyState msg="Loading rejected imports…" />
          : rejectRows.length === 0 ? <PvEmptyState msg="No rejected imports for the selected filters." />
          : (
            <div className="bg-white border border-[#e2e1eb] rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-[12px]" data-testid="pv-rejects-table">
                  <thead className="bg-[#faf8ff] text-[10.5px] text-[#747684] uppercase tracking-wider sticky top-0 z-10">
                    <tr>
                      <th className="px-3 py-2 text-left">Rejected</th>
                      <th className="px-3 py-2 text-left">Location</th>
                      <th className="px-3 py-2 text-left">JC No</th>
                      <th className="px-3 py-2 text-left">Customer</th>
                      <th className="px-3 py-2 text-left">Vehicle</th>
                      <th className="px-3 py-2 text-left">Reason</th>
                      <th className="px-3 py-2 text-left">Remarks</th>
                      <th className="px-3 py-2 text-left">By</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#eeecf6]">
                    {rejectRows.map((r) => (
                      <tr key={r.id} data-testid={`pv-reject-row-${r.id}`} className="hover:bg-[#faf8ff]">
                        <td className="px-3 py-2 whitespace-nowrap">{fmtDT(r.rejected_at)}</td>
                        <td className="px-3 py-2 font-mono">{r.location_code}</td>
                        <td className="px-3 py-2 font-mono font-semibold text-yamaha-700">{r.job_card_no}</td>
                        <td className="px-3 py-2">{r.customer_name || "—"}</td>
                        <td className="px-3 py-2">
                          <div>{r.vehicle_model || "—"}</div>
                          <div className="text-[10.5px] text-[#747684] font-mono">{r.registration_number || ""}</div>
                        </td>
                        <td className="px-3 py-2">
                          <span className="inline-block px-2 py-0.5 rounded-full text-[10.5px] font-semibold bg-rose-50 text-rose-700 border border-rose-200">
                            {r.rejection_reason || "—"}
                          </span>
                        </td>
                        <td className="px-3 py-2 max-w-[240px] truncate text-[#444653]">{r.rejection_remarks || "—"}</td>
                        <td className="px-3 py-2 text-[#747684]">{r.rejected_by || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
      </div>
    );
  }

  const emptyMsg = tab === "active_pending"
    ? "No Pending Vehicles available. Pending Job Cards will appear after the daily Job Card Status Report is uploaded."
    : tab === "ready_for_delivery"
    ? "No vehicles are Ready for Delivery yet."
    : tab === "delivered"
    ? "No delivered vehicles for the selected filters."
    : "No records in this tab for the selected filters.";

  return (
    <div className="max-w-[1400px] mx-auto space-y-4" data-testid="pv-workbench-page">
      {Header} {Tabs} {FiltersBar}

      {loading ? <PvEmptyState msg="Loading Pending Vehicles…" />
        : rows.length === 0 ? <PvEmptyState msg={emptyMsg} />
        : (
          <div className="bg-white border border-[#e2e1eb] rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              {tab === "active_pending" && (
                <ActivePendingTable
                  rows={rows} buffers={buffers} saving={saving} statusOptions={statusOptions}
                  canWrite={canWrite} dirty={dirty} setBuf={setBuf} saveRow={saveRow}
                  onHistory={setHistoryFor} onCnr={setCnrFor} onParts={setPartsFor}
                />
              )}
              {tab === "ready_for_delivery" && (
                <ReadyTable
                  rows={rows} canWrite={canWrite}
                  onDeliver={setDeliverFor} onReturn={setReturnFor} onHistory={setHistoryFor}
                />
              )}
              {tab === "delivered" && (
                <DeliveredTable rows={rows} onHistory={setHistoryFor} />
              )}
            </div>
          </div>
        )}

      {historyFor && (
        <HistoryDrawer
          pv={historyFor}
          onClose={() => setHistoryFor(null)}
          canReopen={canReopen}
          canWrite={canWrite}
          refreshTick={historyRefreshTick}
          onReopen={(rec) => { setHistoryFor(null); setReopenFor(rec); }}
          onParts={(rec) => setPartsFor(rec)}
          onFollowup={(rec) => setFollowupFor(rec)}
        />
      )}

      {cnrFor && (
        <LocTriggerModal
          source={cnrFor}
          sourceType="pv"
          actorName={currentUser?.name}
          actorRole={currentUser?.role}
          onClose={() => setCnrFor(null)}
          onSuccess={() => loadRows()}
        />
      )}

      {deliverFor && (
        <MarkDeliveredDialog
          pv={deliverFor}
          currentUser={currentUser}
          onClose={() => setDeliverFor(null)}
          onDone={() => { setDeliverFor(null); loadRows(); }}
        />
      )}
      {returnFor && (
        <ReturnToActiveDialog
          pv={returnFor}
          statusOptions={statusOptions}
          onClose={() => setReturnFor(null)}
          onDone={() => { setReturnFor(null); loadRows(); }}
        />
      )}
      {reopenFor && (
        <ReopenDialog
          pv={reopenFor}
          statusOptions={statusOptions}
          onClose={() => setReopenFor(null)}
          onDone={() => { setReopenFor(null); loadRows(); }}
        />
      )}
      {partsFor && (
        <UpdatePartsDialog
          pv={partsFor}
          onClose={() => setPartsFor(null)}
          onDone={() => { setPartsFor(null); loadRows(); setHistoryRefreshTick((t) => t + 1); }}
        />
      )}
      {followupFor && (
        <FollowupNoteDialog
          pv={followupFor}
          onClose={() => setFollowupFor(null)}
          onDone={() => { setFollowupFor(null); loadRows(); setHistoryRefreshTick((t) => t + 1); }}
        />
      )}
    </div>
  );
};

// ==================== Active Pending Table ====================
const ActivePendingTable = ({ rows, buffers, saving, statusOptions, canWrite, dirty, setBuf, saveRow, onHistory, onCnr, onParts }) => (
  <table className="w-full text-[12px]" data-testid="pv-wb-table">
    <thead className="bg-[#faf8ff] text-[10.5px] text-[#747684] uppercase tracking-wider sticky top-0 z-10">
      <tr>
        <th className="px-3 py-2 text-left">Location</th>
        <th className="px-3 py-2 text-left">Job Card</th>
        <th className="px-3 py-2 text-left">Customer</th>
        <th className="px-3 py-2 text-left">Vehicle</th>
        <th className="px-3 py-2 text-left">PV Status</th>
        <th className="px-3 py-2 text-left">EDD</th>
        <th className="px-3 py-2 text-left">Latest Remark</th>
        <th className="px-3 py-2 text-center">Ageing</th>
        <th className="px-3 py-2 text-right"></th>
      </tr>
    </thead>
    <tbody className="divide-y divide-[#eeecf6]">
      {rows.map((r) => {
        const b = buffers[r.id] || {};
        const selStatus = b.pv_status_id !== undefined ? b.pv_status_id : (r.pending_vehicle_status_id || "");
        const selEdd    = b.expected_delivery_date !== undefined ? b.expected_delivery_date : (r.expected_delivery_date || "");
        const selRem    = b.latest_remark !== undefined ? b.latest_remark : (r.latest_remark || "");
        const rowDirty  = dirty(r);
        const selDoc = statusOptions.find((s) => s.id === selStatus);
        const needsEdd = selDoc?.edd_mandatory && !(selEdd || "").trim();
        const needsRem = selDoc?.remarks_mandatory && !(selRem || "").trim();
        const editable = canWrite;
        return (
          <tr key={r.id} data-testid={`pv-row-${r.id}`}
            className={`hover:bg-[#faf8ff] ${rowDirty ? "bg-amber-50/40" : ""}`}>
            <td className="px-3 py-2 font-mono text-[11px] text-[#444653]">{r.location_code}</td>
            <td className="px-3 py-2">
              <div className="font-mono font-semibold text-yamaha-700">{r.job_card_no}</div>
              <div className="text-[10.5px] text-[#747684]">JC Date: {fmtDate(r.job_card_date)}</div>
            </td>
            <td className="px-3 py-2">
              <div className="font-medium text-[#1a1b22] flex items-center gap-1.5">
                {r.customer_name || "—"}
                {r.loc_active && <LocActiveBadge caseId={r.active_loc_case_id} testIdPrefix={`pv-row-${r.id}`} />}
              </div>
              <div className="text-[10.5px] text-[#747684] font-mono">{r.registration_number || r.mobile_number || ""}</div>
              {r.registration_number && r.mobile_number && (
                <div className="text-[10px] text-[#a0a2b0]">{r.mobile_number}</div>
              )}
            </td>
            <td className="px-3 py-2">
              <div>{r.vehicle_model || "—"}</div>
              <div className="text-[10.5px] text-[#747684] font-mono">{r.registration_number || ""}</div>
            </td>
            <td className="px-3 py-2 min-w-[190px]">
              {editable ? (
                <select data-testid={`pv-row-status-${r.id}`} value={selStatus}
                  onChange={(e) => setBuf(r.id, { pv_status_id: e.target.value })}
                  className={`w-full border rounded-md px-2 py-1 text-[11.5px] ${needsRem || needsEdd ? "border-rose-400" : "border-[#e2e1eb]"}`}>
                  <option value="">— Select —</option>
                  {statusOptions.map((s) => <option key={s.id} value={s.id}>{s.status_name}</option>)}
                </select>
              ) : (
                <span className="inline-block px-2 py-0.5 rounded-full text-[10.5px] font-semibold border bg-[#faf8ff] text-[#444653] border-[#e2e1eb]">
                  {r.pending_vehicle_status_name || "—"}
                </span>
              )}
            </td>
            <td className="px-3 py-2 min-w-[140px]">
              {editable ? (
                <input type="date" data-testid={`pv-row-edd-${r.id}`} value={selEdd || ""}
                  onChange={(e) => setBuf(r.id, { expected_delivery_date: e.target.value })}
                  className={`w-full border rounded-md px-2 py-1 text-[11.5px] ${needsEdd ? "border-rose-400" : "border-[#e2e1eb]"}`} />
              ) : <span className="text-[#444653]">{fmtDate(r.expected_delivery_date)}</span>}
              <PartsIndicator row={r} />
            </td>
            <td className="px-3 py-2 min-w-[220px]">
              {editable ? (
                <input data-testid={`pv-row-remark-${r.id}`} value={selRem}
                  onChange={(e) => setBuf(r.id, { latest_remark: e.target.value })}
                  placeholder="Add a remark…"
                  className={`w-full border rounded-md px-2 py-1 text-[11.5px] ${needsRem ? "border-rose-400" : "border-[#e2e1eb]"}`} />
              ) : <div className="text-[#444653] max-w-[260px] truncate">{r.latest_remark || "—"}</div>}
            </td>
            <td className="px-3 py-2 text-center">
              <span className={`inline-block px-2 py-0.5 rounded-full text-[10.5px] font-semibold border ${AGE_TONE(r.ageing_bucket)}`}
                title={`${r.ageing_days ?? "?"} day(s)`}>
                {r.ageing_days ?? "—"} d
              </span>
            </td>
            <td className="px-3 py-2 text-right whitespace-nowrap">
              <div className="inline-flex items-center gap-1">
                <button onClick={() => onHistory(r)} data-testid={`pv-row-history-${r.id}`}
                  title="View history" className="p-1.5 rounded hover:bg-[#faf8ff] text-[#747684] hover:text-yamaha-700">
                  <History className="w-3.5 h-3.5" />
                </button>
                {canWrite && !r.loc_active && (
                  <button onClick={() => onCnr(r)} data-testid={`pv-row-cnr-${r.id}`}
                    title="Customer Not Responding" className="p-1.5 rounded hover:bg-amber-50 text-[#747684] hover:text-amber-600">
                    <AlertTriangle className="w-3.5 h-3.5" />
                  </button>
                )}
                {canWrite && (
                  <button onClick={() => onParts(r)} data-testid={`pv-row-parts-btn-${r.id}`}
                    title="Update Parts" className="p-1.5 rounded hover:bg-sky-50 text-[#747684] hover:text-sky-700">
                    <Package className="w-3.5 h-3.5" />
                  </button>
                )}
                {editable && (
                  <button disabled={!rowDirty || saving[r.id]} onClick={() => saveRow(r)}
                    data-testid={`pv-row-save-${r.id}`}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11.5px] font-semibold border ${
                      rowDirty ? "bg-yamaha-700 hover:bg-yamaha-800 text-white border-yamaha-700"
                              : "bg-white text-[#a09fb0] border-[#e2e1eb] cursor-not-allowed"}`}>
                    {saving[r.id] ? <RefreshCw className="w-3 h-3 animate-spin" />
                      : rowDirty ? <><Save className="w-3 h-3" /> Save</>
                      : <><Check className="w-3 h-3" /> Saved</>}
                  </button>
                )}
              </div>
            </td>
          </tr>
        );
      })}
    </tbody>
  </table>
);

// ==================== Ready for Delivery Table ====================
const ReadyTable = ({ rows, canWrite, onDeliver, onReturn, onHistory }) => (
  <table className="w-full text-[12px]" data-testid="pv-ready-table">
    <thead className="bg-[#faf8ff] text-[10.5px] text-[#747684] uppercase tracking-wider sticky top-0 z-10">
      <tr>
        <th className="px-3 py-2 text-center">Ready Since</th>
        <th className="px-3 py-2 text-left">Job Card</th>
        <th className="px-3 py-2 text-left">Customer</th>
        <th className="px-3 py-2 text-left">Vehicle</th>
        <th className="px-3 py-2 text-left">PV Status</th>
        <th className="px-3 py-2 text-left">Work Completed</th>
        <th className="px-3 py-2 text-left">Latest Remark</th>
        <th className="px-3 py-2 text-left">Last Updated By</th>
        <th className="px-3 py-2 text-right">Actions</th>
      </tr>
    </thead>
    <tbody className="divide-y divide-[#eeecf6]">
      {rows.map((r) => (
        <tr key={r.id} data-testid={`pv-ready-row-${r.id}`}
          className={`hover:bg-[#faf8ff] ${r.ready_since_bucket === "gt2d" ? "bg-rose-50/40" : ""}`}>
          <td className="px-3 py-2 text-center">
            <span className={`inline-block px-2 py-0.5 rounded-full text-[10.5px] font-semibold border ${READY_TONE(r.ready_since_bucket)}`}
              title={`${r.ready_since_days ?? "?"} day(s)`}>
              {READY_LABEL(r.ready_since_bucket, r.ready_since_days)}
            </span>
          </td>
          <td className="px-3 py-2">
            <div className="font-mono font-semibold text-yamaha-700">{r.job_card_no}</div>
            <div className="text-[10.5px] text-[#747684]">JC Date: {fmtDate(r.job_card_date)}</div>
            <div className="text-[10.5px] text-[#747684] font-mono">{r.location_code}</div>
          </td>
          <td className="px-3 py-2">
            <div className="font-medium text-[#1a1b22]">{r.customer_name || "—"}</div>
            <div className="text-[10.5px] text-[#747684] font-mono">{r.mobile_number || ""}</div>
          </td>
          <td className="px-3 py-2">
            <div>{r.vehicle_model || "—"}</div>
            <div className="text-[10.5px] text-[#747684] font-mono">{r.registration_number || ""}</div>
          </td>
          <td className="px-3 py-2">
            <span className="inline-block px-2 py-0.5 rounded-full text-[10.5px] font-semibold border bg-teal-50 text-teal-700 border-teal-200">
              {r.pending_vehicle_status_name || "—"}
            </span>
          </td>
          <td className="px-3 py-2 whitespace-nowrap text-[#444653]">{fmtDT(r.work_completed_at)}</td>
          <td className="px-3 py-2 max-w-[240px] truncate text-[#444653]">{r.latest_remark || "—"}</td>
          <td className="px-3 py-2 text-[#747684]">{r.last_updated_by || "—"}</td>
          <td className="px-3 py-2 text-right whitespace-nowrap">
            <div className="inline-flex items-center gap-1">
              {canWrite && (
                <>
                  <button data-testid={`pv-ready-deliver-${r.id}`} onClick={() => onDeliver(r)}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold bg-emerald-600 hover:bg-emerald-700 text-white">
                    <PackageCheck className="w-3 h-3" /> Mark Delivered
                  </button>
                  <button data-testid={`pv-ready-return-${r.id}`} onClick={() => onReturn(r)}
                    title="Return to Active Pending"
                    className="p-1.5 rounded hover:bg-amber-50 text-amber-700">
                    <Undo2 className="w-3.5 h-3.5" />
                  </button>
                </>
              )}
              <button onClick={() => onHistory(r)} data-testid={`pv-ready-history-${r.id}`}
                title="View history" className="p-1.5 rounded hover:bg-[#faf8ff] text-[#747684] hover:text-yamaha-700">
                <History className="w-3.5 h-3.5" />
              </button>
            </div>
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

// ==================== Delivered Table ====================
const DeliveredTable = ({ rows, onHistory }) => (
  <table className="w-full text-[12px]" data-testid="pv-delivered-table">
    <thead className="bg-[#faf8ff] text-[10.5px] text-[#747684] uppercase tracking-wider sticky top-0 z-10">
      <tr>
        <th className="px-3 py-2 text-left">Job Card</th>
        <th className="px-3 py-2 text-left">Customer</th>
        <th className="px-3 py-2 text-left">Vehicle</th>
        <th className="px-3 py-2 text-left">Work Completed</th>
        <th className="px-3 py-2 text-left">Delivered</th>
        <th className="px-3 py-2 text-center">Total Pending Days</th>
        <th className="px-3 py-2 text-left">Delivered By</th>
        <th className="px-3 py-2 text-left">Final Remark</th>
        <th className="px-3 py-2 text-right"></th>
      </tr>
    </thead>
    <tbody className="divide-y divide-[#eeecf6]">
      {rows.map((r) => (
        <tr key={r.id} data-testid={`pv-delivered-row-${r.id}`} className="hover:bg-[#faf8ff]">
          <td className="px-3 py-2">
            <div className="font-mono font-semibold text-yamaha-700">{r.job_card_no}</div>
            <div className="text-[10.5px] text-[#747684]">JC Date: {fmtDate(r.job_card_date)}</div>
            <div className="text-[10.5px] text-[#747684] font-mono">{r.location_code}</div>
          </td>
          <td className="px-3 py-2">
            <div className="font-medium text-[#1a1b22]">{r.customer_name || "—"}</div>
            <div className="text-[10.5px] text-[#747684] font-mono">{r.mobile_number || ""}</div>
          </td>
          <td className="px-3 py-2">
            <div>{r.vehicle_model || "—"}</div>
            <div className="text-[10.5px] text-[#747684] font-mono">{r.registration_number || ""}</div>
          </td>
          <td className="px-3 py-2 whitespace-nowrap text-[#444653]">{fmtDT(r.work_completed_at)}</td>
          <td className="px-3 py-2 whitespace-nowrap text-[#444653]">{fmtDT(r.delivery_at)}</td>
          <td className="px-3 py-2 text-center">
            <span className={`inline-block px-2 py-0.5 rounded-full text-[10.5px] font-semibold border ${AGE_TONE(r.pending_days_bucket)}`}>
              {r.total_pending_days ?? "—"} d
            </span>
          </td>
          <td className="px-3 py-2 text-[#444653]">{r.delivered_by || "—"}</td>
          <td className="px-3 py-2 max-w-[260px] truncate text-[#444653]">{r.latest_remark || "—"}</td>
          <td className="px-3 py-2 text-right whitespace-nowrap">
            <button onClick={() => onHistory(r)} data-testid={`pv-delivered-history-${r.id}`}
              title="View history" className="p-1.5 rounded hover:bg-[#faf8ff] text-[#747684] hover:text-yamaha-700">
              <History className="w-3.5 h-3.5" />
            </button>
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

// ==================== Mark Delivered Dialog ====================
const MarkDeliveredDialog = ({ pv, currentUser, onClose, onDone }) => {
  const [date, setDate] = useState(nowDate());
  const [time, setTime] = useState(nowTime());
  const [remark, setRemark] = useState("");
  const [by, setBy] = useState(currentUser?.name || "");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!date) return toast.error("Delivery Date is required.");
    if (!time) return toast.error("Delivery Time is required.");
    setBusy(true);
    try {
      await pvMarkDelivered(pv.id, {
        delivery_date: date, delivery_time: time,
        final_remark: remark || null, delivered_by: by || null,
      });
      toast.success("Vehicle marked as delivered successfully.");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to mark delivered");
    } finally { setBusy(false); }
  };

  return (
    <ModalShell title="Mark Delivered" icon={<PackageCheck className="w-5 h-5 text-emerald-700" />}
      subtitle={`${pv.job_card_no} · ${pv.customer_name || "—"}`} onClose={onClose} testId="pv-deliver-dialog">
      <FormRow label="Delivery Date *">
        <input type="date" data-testid="pv-deliver-date" value={date} onChange={(e) => setDate(e.target.value)}
          className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px]" />
      </FormRow>
      <FormRow label="Delivery Time *">
        <input type="time" data-testid="pv-deliver-time" value={time} onChange={(e) => setTime(e.target.value)}
          className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px]" />
      </FormRow>
      <FormRow label="Final Remark">
        <textarea data-testid="pv-deliver-remark" value={remark} onChange={(e) => setRemark(e.target.value)} rows={3}
          className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px]" placeholder="Optional handover notes" />
      </FormRow>
      <FormRow label="Delivered By">
        <input data-testid="pv-deliver-by" value={by} onChange={(e) => setBy(e.target.value)}
          className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px]" />
      </FormRow>
      <ModalActions>
        <button onClick={onClose} className="px-4 py-2 rounded-lg border border-[#e2e1eb] text-[12px]">Cancel</button>
        <button onClick={submit} disabled={busy} data-testid="pv-deliver-confirm"
          className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-[12px] font-semibold inline-flex items-center gap-1.5">
          <CheckCircle2 className="w-3.5 h-3.5" /> {busy ? "Delivering…" : "Confirm Delivery"}
        </button>
      </ModalActions>
    </ModalShell>
  );
};

// ==================== Return to Active Dialog ====================
const ReturnToActiveDialog = ({ pv, statusOptions, onClose, onDone }) => {
  const activeStatuses = statusOptions.filter((s) => s.status_category === "active_pending" && s.is_active);
  const [statusId, setStatusId] = useState("");
  const [edd, setEdd] = useState("");
  const [remark, setRemark] = useState("");
  const [busy, setBusy] = useState(false);
  const sel = activeStatuses.find((s) => s.id === statusId);

  const submit = async () => {
    if (!statusId) return toast.error("Please select a PV Status.");
    if (!(remark || "").trim()) return toast.error("Reason / Remark is required.");
    if (sel?.edd_mandatory && !(edd || "").trim())
      return toast.error("Expected Delivery Date is required for the selected status.");
    setBusy(true);
    try {
      await pvReturnToActive(pv.id, {
        pv_status_id: statusId, expected_delivery_date: edd || null, remark: remark.trim(),
      });
      toast.success("Vehicle returned to Active Pending.");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to return to Active Pending");
    } finally { setBusy(false); }
  };

  return (
    <ModalShell title="Return to Active Pending" icon={<Undo2 className="w-5 h-5 text-amber-700" />}
      subtitle={`${pv.job_card_no} · ${pv.customer_name || "—"}`} onClose={onClose} testId="pv-return-dialog">
      <FormRow label="PV Status *">
        <select data-testid="pv-return-status" value={statusId} onChange={(e) => setStatusId(e.target.value)}
          className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px]">
          <option value="">— Select an Active Pending status —</option>
          {activeStatuses.map((s) => <option key={s.id} value={s.id}>{s.status_name}</option>)}
        </select>
      </FormRow>
      <FormRow label={`Expected Delivery Date${sel?.edd_mandatory ? " *" : ""}`}>
        <input type="date" data-testid="pv-return-edd" value={edd} onChange={(e) => setEdd(e.target.value)}
          className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px]" />
      </FormRow>
      <FormRow label="Reason / Remark *">
        <textarea data-testid="pv-return-remark" value={remark} onChange={(e) => setRemark(e.target.value)} rows={3}
          className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px]" placeholder="Why is this returning to Active Pending?" />
      </FormRow>
      <ModalActions>
        <button onClick={onClose} className="px-4 py-2 rounded-lg border border-[#e2e1eb] text-[12px]">Cancel</button>
        <button onClick={submit} disabled={busy} data-testid="pv-return-confirm"
          className="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-[12px] font-semibold inline-flex items-center gap-1.5">
          <RotateCcw className="w-3.5 h-3.5" /> {busy ? "Returning…" : "Confirm Return"}
        </button>
      </ModalActions>
    </ModalShell>
  );
};

// ==================== Reopen Delivered Dialog ====================
const ReopenDialog = ({ pv, statusOptions, onClose, onDone }) => {
  const activeStatuses = statusOptions.filter((s) => s.status_category === "active_pending" && s.is_active);
  const [statusId, setStatusId] = useState("");
  const [edd, setEdd] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const sel = activeStatuses.find((s) => s.id === statusId);

  const submit = async () => {
    if (!statusId) return toast.error("Please select a PV Status.");
    if (!(reason || "").trim()) return toast.error("Reopen Reason is required.");
    if (sel?.edd_mandatory && !(edd || "").trim())
      return toast.error("Expected Delivery Date is required for the selected status.");
    setBusy(true);
    try {
      await pvReopenDelivered(pv.id, {
        pv_status_id: statusId, expected_delivery_date: edd || null, reason: reason.trim(),
      });
      toast.success("Delivered record reopened successfully.");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reopen record");
    } finally { setBusy(false); }
  };

  return (
    <ModalShell title="Reopen Delivered Record" icon={<RotateCcw className="w-5 h-5 text-purple-700" />}
      subtitle={`${pv.job_card_no} · ${pv.customer_name || "—"}`} onClose={onClose} testId="pv-reopen-dialog">
      <FormRow label="New Active Pending Status *">
        <select data-testid="pv-reopen-status" value={statusId} onChange={(e) => setStatusId(e.target.value)}
          className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px]">
          <option value="">— Select an Active Pending status —</option>
          {activeStatuses.map((s) => <option key={s.id} value={s.id}>{s.status_name}</option>)}
        </select>
      </FormRow>
      <FormRow label={`Expected Delivery Date${sel?.edd_mandatory ? " *" : ""}`}>
        <input type="date" data-testid="pv-reopen-edd" value={edd} onChange={(e) => setEdd(e.target.value)}
          className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px]" />
      </FormRow>
      <FormRow label="Reopen Reason *">
        <textarea data-testid="pv-reopen-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
          className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px]" placeholder="Why is this delivered record being reopened?" />
      </FormRow>
      <ModalActions>
        <button onClick={onClose} className="px-4 py-2 rounded-lg border border-[#e2e1eb] text-[12px]">Cancel</button>
        <button onClick={submit} disabled={busy} data-testid="pv-reopen-confirm"
          className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-[12px] font-semibold inline-flex items-center gap-1.5">
          <RotateCcw className="w-3.5 h-3.5" /> {busy ? "Reopening…" : "Confirm Reopen"}
        </button>
      </ModalActions>
    </ModalShell>
  );
};

// ==================== Update Parts Dialog ====================
const UpdatePartsDialog = ({ pv, onClose, onDone }) => {
  const [ordered, setOrdered] = useState(!!pv.parts_ordered);
  const [poNumber, setPoNumber] = useState(pv.parts_po_number || "");
  const [poDate, setPoDate] = useState(pv.parts_po_date || "");
  const [edd, setEdd] = useState(pv.parts_edd || "");
  const [status, setStatus] = useState(pv.parts_status || "ordered");
  const [remark, setRemark] = useState(pv.parts_remark || "");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (ordered) {
      if (!(poNumber || "").trim()) return toast.error("PO Number is required.");
      if (!edd) return toast.error("EDD is required.");
    }
    setBusy(true);
    try {
      await pvUpdateParts(pv.id, {
        parts_ordered: ordered,
        po_number: poNumber || null, po_date: poDate || null, edd: edd || null,
        parts_status: status, parts_remark: remark || null,
      });
      toast.success("Parts updated.");
      onDone();
    } catch (e) {
      toast.error(formatErr(e, "Failed to update parts"));
    } finally { setBusy(false); }
  };

  return (
    <ModalShell title="Update Parts" icon={<Package className="w-5 h-5 text-sky-700" />}
      subtitle={`${pv.job_card_no} · ${pv.customer_name || "—"}`} onClose={onClose} testId="pv-parts-dialog">
      <label className="flex items-center gap-2 text-[13px] font-medium text-[#1a1b22]">
        <input type="checkbox" data-testid="pv-parts-ordered-toggle" checked={ordered}
          onChange={(e) => setOrdered(e.target.checked)} className="w-4 h-4 accent-yamaha-700" />
        Parts Ordered
      </label>
      {ordered && (
        <>
          <FormRow label="PO Number *">
            <input data-testid="pv-parts-po-number" value={poNumber} onChange={(e) => setPoNumber(e.target.value)}
              className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px]" />
          </FormRow>
          <FormRow label="PO Date">
            <input type="date" data-testid="pv-parts-po-date" value={poDate} onChange={(e) => setPoDate(e.target.value)}
              className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px]" />
          </FormRow>
          <FormRow label="EDD *">
            <input type="date" data-testid="pv-parts-edd" value={edd} onChange={(e) => setEdd(e.target.value)}
              className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px]" />
          </FormRow>
          <FormRow label="Parts Status">
            <select data-testid="pv-parts-status" value={status} onChange={(e) => setStatus(e.target.value)}
              className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px]">
              {Object.entries(PARTS_STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </FormRow>
          <FormRow label="Parts Remark">
            <textarea data-testid="pv-parts-remark" value={remark} onChange={(e) => setRemark(e.target.value)} rows={2}
              className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px]" placeholder="Optional" />
          </FormRow>
        </>
      )}
      <ModalActions>
        <button onClick={onClose} className="px-4 py-2 rounded-lg border border-[#e2e1eb] text-[12px]">Cancel</button>
        <button onClick={submit} disabled={busy} data-testid="pv-parts-confirm"
          className="px-4 py-2 rounded-lg bg-sky-600 hover:bg-sky-700 disabled:opacity-50 text-white text-[12px] font-semibold inline-flex items-center gap-1.5">
          <Package className="w-3.5 h-3.5" /> {busy ? "Saving…" : "Save Parts"}
        </button>
      </ModalActions>
    </ModalShell>
  );
};

// ==================== Follow-up Note Dialog ====================
const FOLLOWUP_MODES = [["call", "Call"], ["whatsapp", "WhatsApp"], ["walk_in", "Walk-In"], ["other", "Other"]];
const FollowupNoteDialog = ({ pv, onClose, onDone }) => {
  const [mode, setMode] = useState("call");
  const [remark, setRemark] = useState("");
  const [date, setDate] = useState(nowDate());
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!(remark || "").trim()) return toast.error("Remark is required.");
    setBusy(true);
    try {
      await pvAddFollowup(pv.id, { mode, remark: remark.trim(), followup_date: date });
      toast.success("Follow-up note added.");
      onDone();
    } catch (e) {
      toast.error(formatErr(e, "Failed to add follow-up note"));
    } finally { setBusy(false); }
  };

  return (
    <ModalShell title="Add Follow-up Note" icon={<MessageSquarePlus className="w-5 h-5 text-yamaha-700" />}
      subtitle={`${pv.job_card_no} · ${pv.customer_name || "—"}`} onClose={onClose} testId="pv-followup-dialog">
      <FormRow label="Mode">
        <select data-testid="pv-followup-mode" value={mode} onChange={(e) => setMode(e.target.value)}
          className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px]">
          {FOLLOWUP_MODES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </FormRow>
      <FormRow label="Follow-up Date">
        <input type="date" data-testid="pv-followup-date" value={date} onChange={(e) => setDate(e.target.value)}
          className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px]" />
      </FormRow>
      <FormRow label="Remark *">
        <textarea data-testid="pv-followup-remark" value={remark} onChange={(e) => setRemark(e.target.value)} rows={3}
          className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px]" placeholder="e.g. Customer requested 2 more days." />
      </FormRow>
      <ModalActions>
        <button onClick={onClose} className="px-4 py-2 rounded-lg border border-[#e2e1eb] text-[12px]">Cancel</button>
        <button onClick={submit} disabled={busy} data-testid="pv-followup-confirm"
          className="px-4 py-2 rounded-lg bg-yamaha-700 hover:bg-yamaha-800 disabled:opacity-50 text-white text-[12px] font-semibold inline-flex items-center gap-1.5">
          <MessageSquarePlus className="w-3.5 h-3.5" /> {busy ? "Saving…" : "Add Note"}
        </button>
      </ModalActions>
    </ModalShell>
  );
};

// ==================== Modal utilities ====================
const ModalShell = ({ title, subtitle, icon, onClose, children, testId }) => (
  <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid={testId}>
    <div className="bg-white rounded-xl border border-[#e2e1eb] w-full max-w-md shadow-xl">
      <div className="px-5 py-3 border-b border-[#e2e1eb] flex items-start gap-2">
        {icon && <div className="w-8 h-8 rounded-lg bg-[#faf8ff] flex items-center justify-center">{icon}</div>}
        <div className="flex-1">
          <h3 className="text-[15px] font-semibold text-[#1a1b22]">{title}</h3>
          {subtitle && <div className="text-[11.5px] text-[#747684] mt-0.5">{subtitle}</div>}
        </div>
        <button onClick={onClose} className="text-[#747684] hover:text-[#1a1b22]"><X className="w-4 h-4" /></button>
      </div>
      <div className="p-5 space-y-3">{children}</div>
    </div>
  </div>
);
const FormRow = ({ label, children }) => (
  <div>
    <label className="text-[11px] font-semibold uppercase tracking-wider text-[#747684] block mb-1">{label}</label>
    {children}
  </div>
);
const ModalActions = ({ children }) => (
  <div className="px-5 py-3 border-t border-[#e2e1eb] flex justify-end gap-2 -mx-5 -mb-5 mt-3 bg-[#fafafd] rounded-b-xl">
    {children}
  </div>
);

// -------- History Drawer -----------------------------------------------
const ACTION_LABELS = {
  CREATED:                  { label: "Created",              tone: "bg-emerald-50 text-emerald-700" },
  PV_IMPORTED:              { label: "Imported from Report", tone: "bg-blue-50 text-blue-700" },
  EXISTING_UPDATED:         { label: "Report Refresh",       tone: "bg-blue-50 text-blue-700" },
  REPORT_UPLOADED:          { label: "Report Uploaded",      tone: "bg-blue-50 text-blue-700" },
  STATUS_CHANGED:           { label: "Status Changed",       tone: "bg-purple-50 text-purple-700" },
  EDD_UPDATED:              { label: "EDD Updated",          tone: "bg-amber-50 text-amber-700" },
  REMARK_UPDATED:           { label: "Remark Updated",       tone: "bg-[#faf8ff] text-[#444653]" },
  MOVED_TO_READY:           { label: "Moved to Ready",       tone: "bg-teal-50 text-teal-700" },
  DELIVERED:                { label: "Delivered",            tone: "bg-emerald-50 text-emerald-700" },
  MARKED_DELIVERED:         { label: "Marked Delivered",     tone: "bg-emerald-50 text-emerald-700" },
  RETURNED_TO_ACTIVE:       { label: "Returned to Active",   tone: "bg-amber-50 text-amber-700" },
  REOPENED:                 { label: "Reopened",             tone: "bg-purple-50 text-purple-700" },
  CANCELLED:                { label: "Cancelled",            tone: "bg-rose-50 text-rose-700" },
  MIGRATED:                 { label: "Migrated",             tone: "bg-blue-50 text-blue-700" },
  PO_UPDATED:               { label: "Parts PO Updated",     tone: "bg-sky-50 text-sky-700" },
  FOLLOWUP_LOGGED:          { label: "Customer Follow-up",   tone: "bg-yamaha-50 text-yamaha-700" },
  LOC_TRIGGERED:            { label: "LOC Triggered",        tone: "bg-amber-50 text-amber-700" },
  LOC_RESUMED:              { label: "Repair Resumed (LOC)", tone: "bg-amber-50 text-amber-700" },
  LOC_CLOSED:               { label: "LOC Closed",           tone: "bg-amber-50 text-amber-700" },
};

const HistoryDrawer = ({ pv, onClose, canReopen, canWrite, onReopen, onParts, onFollowup, refreshTick }) => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [record, setRecord] = useState(pv);
  const [showAllLogs, setShowAllLogs] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const d = await pvHistory(pv.id);
        if (!alive) return;
        setLogs(d.logs || []);
        setRecord(d.record || pv);
      } catch (e) {
        toast.error(formatErr(e, "Failed to load history"));
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [pv.id, refreshTick]);

  const isDelivered = record.record_state === "delivered";
  const showReopen = isDelivered && canReopen;
  const visibleLogs = showAllLogs ? logs : logs.slice(0, 3);

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="pv-history-drawer">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-[460px] bg-white border-l border-[#e2e1eb] h-full overflow-y-auto shadow-xl">
        <div className="px-5 py-4 border-b border-[#e2e1eb] flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-yamaha-50 flex items-center justify-center">
            <History className="w-5 h-5 text-yamaha-700" />
          </div>
          <div className="flex-1">
            <h3 className="text-[15px] font-semibold text-[#1a1b22]">Vehicle History</h3>
            <div className="text-[11.5px] text-[#747684] mt-0.5">
              <span className="font-mono font-semibold text-yamaha-700">{record.job_card_no}</span>
              {" · "}{record.location_code}
            </div>
          </div>
          <button onClick={onClose} data-testid="pv-history-close" className="text-[#747684] hover:text-[#1a1b22]">
            <X className="w-4 h-4" />
          </button>
        </div>

        {canWrite && (
          <div className="px-5 py-3 border-b border-[#e2e1eb] bg-[#fafafd]">
            <div className="text-[10.5px] font-semibold uppercase tracking-wider text-[#747684] mb-2">Quick Actions</div>
            <div className="flex flex-wrap gap-1.5">
              {!isDelivered && (
                <button onClick={() => onParts(record)} data-testid="pv-history-qa-parts"
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-sky-600 hover:bg-sky-700 text-white text-[11.5px] font-semibold">
                  <Package className="w-3.5 h-3.5" /> Update Parts
                </button>
              )}
              <button onClick={() => onFollowup(record)} data-testid="pv-history-qa-followup"
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-yamaha-700 hover:bg-yamaha-800 text-white text-[11.5px] font-semibold">
                <MessageSquarePlus className="w-3.5 h-3.5" /> Add Follow-up Note
              </button>
              {record.loc_active && record.active_loc_case_id && (
                <button onClick={() => navigate(`/loc/${record.active_loc_case_id}`)} data-testid="pv-history-qa-loc"
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800 text-[11.5px] font-semibold">
                  <ExternalLink className="w-3.5 h-3.5" /> View LOC Case
                </button>
              )}
              {showReopen && (
                <button data-testid="pv-history-reopen" onClick={() => onReopen(record)}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-purple-600 hover:bg-purple-700 text-white text-[11.5px] font-semibold">
                  <RotateCcw className="w-3.5 h-3.5" /> Reopen Delivered Record
                </button>
              )}
            </div>
          </div>
        )}

        <div className="px-5 py-4 border-b border-[#e2e1eb]">
          <h4 className="text-[10.5px] font-semibold uppercase tracking-wider text-[#747684] mb-2">Vehicle Summary</h4>
          <div className="grid grid-cols-2 gap-3">
            <Detail label="Customer Name" value={record.customer_name} />
            <Detail label="Mobile" value={record.mobile_number} mono />
            <Detail label="Vehicle Model" value={record.vehicle_model} />
            <Detail label="Registration No." value={record.registration_number} mono />
            <Detail label="Service Type" value={record.service_type} />
          </div>
        </div>

        <div className="px-5 py-4 border-b border-[#e2e1eb] bg-[#fafafd]">
          <h4 className="text-[10.5px] font-semibold uppercase tracking-wider text-[#747684] mb-2">Current Case</h4>
          <div className="grid grid-cols-2 gap-3">
            <Detail label="PV Status" value={record.pending_vehicle_status_name} />
            <Detail label="Record State" value={TABS.find((t) => t.key === record.record_state)?.label} />
            <Detail label="EDD" value={record.expected_delivery_date ? fmtDate(record.expected_delivery_date) : null} />
            {record.parts_ordered && (
              <>
                <Detail label="Parts Ordered" value="Yes" />
                <Detail label="PO Number" value={record.parts_po_number} mono />
                <Detail label="PO Date" value={record.parts_po_date ? fmtDate(record.parts_po_date) : null} />
                <Detail label="Parts EDD" value={record.parts_edd ? fmtDate(record.parts_edd) : null} />
                <Detail label="Parts Status" value={PARTS_STATUS_LABELS[record.parts_status]} />
              </>
            )}
            <Detail label="Delivery Date" value={record.delivery_at ? fmtDT(record.delivery_at) : null} />
            <Detail label="Delivered By" value={record.delivered_by} />
          </div>
          {(record.latest_remark || record.parts_remark) && (
            <div className="mt-3 space-y-2">
              {record.latest_remark && (
                <div>
                  <div className="text-[10.5px] uppercase tracking-wider text-[#747684] font-semibold">Latest Remark</div>
                  <div className="text-[12.5px] text-[#1a1b22] mt-0.5">{record.latest_remark}</div>
                </div>
              )}
              {record.parts_ordered && record.parts_remark && (
                <div>
                  <div className="text-[10.5px] uppercase tracking-wider text-[#747684] font-semibold">Parts Remark</div>
                  <div className="text-[12.5px] text-[#1a1b22] mt-0.5">{record.parts_remark}</div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-5 py-4">
          <h4 className="text-[11.5px] font-semibold uppercase tracking-wider text-[#747684] mb-3">Activity Timeline</h4>
          {loading ? (
            <div className="text-[12.5px] text-[#747684]">Loading history…</div>
          ) : logs.length === 0 ? (
            <div className="flex items-center gap-2 text-[12.5px] text-[#747684]">
              <AlertCircle className="w-3.5 h-3.5" /> No activity recorded yet.
            </div>
          ) : (
            <>
              <div className="space-y-3">
                {visibleLogs.map((l, i) => {
                  const meta = ACTION_LABELS[l.action_type] || { label: l.action_type, tone: "bg-[#f4f3fc] text-[#444653]" };
                  return (
                    <div key={l.id || i} className="border-l-2 border-yamaha-200 pl-3 py-1" data-testid={`pv-history-entry-${i}`}>
                      <div className="flex items-center gap-2">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[10.5px] font-semibold ${meta.tone}`}>
                          {meta.label}
                        </span>
                        <span className="text-[10.5px] text-[#747684]">{fmtDT(l.performed_at)}</span>
                      </div>
                      {(l.previous_value || l.new_value) && (
                        <div className="text-[11.5px] text-[#444653] mt-1 flex items-center gap-1 flex-wrap">
                          <span className="text-[#747684]">{l.previous_value || "—"}</span>
                          <ChevronRight className="w-3 h-3 text-[#747684]" />
                          <span className="font-semibold text-[#1a1b22]">{l.new_value || "—"}</span>
                        </div>
                      )}
                      {l.remarks && <div className="text-[11.5px] text-[#444653] mt-1">{l.remarks}</div>}
                      <div className="text-[10.5px] text-[#747684] mt-0.5">by {l.performed_by || "System"}</div>
                    </div>
                  );
                })}
              </div>
              {logs.length > 3 && (
                <button onClick={() => setShowAllLogs((s) => !s)} data-testid="pv-history-view-more"
                  className="mt-3 text-[11.5px] font-semibold text-yamaha-700 hover:underline">
                  {showAllLogs ? "Show Less" : `View More (${logs.length - 3})`}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Detail = ({ label, value, mono = false }) => {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-wider text-[#747684] font-semibold">{label}</div>
      <div className={`text-[12.5px] text-[#1a1b22] mt-0.5 ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
};

export default PvWorkbench;
